<template>
  <component
    :is="componentTag"
    :class="classes"
    :href="componentTag === 'a' ? props.href : undefined"
    :type="componentTag === 'button' ? props.type : undefined"
    :disabled="componentTag === 'button' ? isDisabled : undefined"
    :aria-disabled="componentTag !== 'button' && isDisabled ? 'true' : undefined"
    :aria-busy="props.loading || undefined"
    :aria-label="props.ariaLabel || undefined"
    @click="handleClick"
  >
    <span class="sf-button__inner">
      <span v-if="props.loading" class="sf-button__spinner" aria-hidden="true"></span>
      <span v-if="$slots['icon-left']" class="sf-button__icon sf-button__icon--left">
        <slot name="icon-left"></slot>
      </span>
      <span class="sf-button__content">
        <slot>{{ props.label }}</slot>
      </span>
      <span v-if="$slots['icon-right']" class="sf-button__icon sf-button__icon--right">
        <slot name="icon-right"></slot>
      </span>
    </span>
  </component>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { ButtonProps } from './types'

defineOptions({ name: 'AppButton' })

const props = withDefaults(defineProps<ButtonProps>(), {
  variant: 'primary',
  size: 'md',
  fullWidth: false,
  loading: false,
  disabled: false,
  type: 'button',
})

const componentTag = computed(() => (props.href ? 'a' : 'button'))
const isButtonTag = computed(() => componentTag.value === 'button')
const isDisabled = computed(() => props.disabled || props.loading)

const classes = computed(() => [
  'sf-button',
  `sf-button--${props.variant}`,
  `sf-button--${props.size}`,
  {
    'sf-button--full': props.fullWidth,
    'is-loading': props.loading,
    'is-disabled': isDisabled.value,
  },
])

function handleClick(event: MouseEvent) {
  if (!isDisabled.value) return
  event.preventDefault()
  if (!isButtonTag.value) {
    event.stopImmediatePropagation()
  }
}
</script>

<style scoped lang="scss">
@use 'sass:color';
@use '@/styles/_tokens.scss' as *;

.sf-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 10px 16px;
  border-radius: $radius-sm;
  border: 1px solid transparent;
  font-weight: 600;
  font-size: 0.95rem;
  color: $text-light-200;
  background: color.scale($surface, $lightness: 6%);
  transition:
    background-color 160ms ease,
    border-color 160ms ease,
    box-shadow 160ms ease,
    color 160ms ease,
    transform 120ms ease;
  cursor: pointer;
  position: relative;
  min-height: $button-height-md;

  &.sf-button--full {
    width: 100%;
  }

  &:focus-visible {
    outline: 2px solid color.scale($accent-lime, $lightness: 10%);
    outline-offset: 3px;
  }

  &:active {
    transform: scale(0.98);
  }

  &.is-disabled {
    cursor: not-allowed;
    opacity: 0.65;
    pointer-events: none;
  }

  &.is-loading {
    pointer-events: none;
  }

  &--sm {
    padding: 8px 12px;
    font-size: 0.875rem;
    min-height: $button-height-sm;
  }

  &--lg {
    padding: 12px 20px;
    font-size: 1rem;
    min-height: $button-height-lg;
  }

  &--primary {
    background: $green-button-bg;
    border-color: $accent-lime;
    color: $text-light-200;
    box-shadow:
      0 0 0 1px color.scale($green-button-bg, $lightness: -12%),
      0 6px 20px rgba(0, 0, 0, 0.35);

    &:hover:not(.is-disabled):not(.is-loading) {
      background: color.scale($green-button-bg, $lightness: 6%);
      border-color: color.scale($accent-lime, $lightness: -6%);
    }

    &:active:not(.is-disabled):not(.is-loading) {
      background: color.scale($green-button-bg, $lightness: -6%);
      border-color: color.scale($accent-lime, $lightness: -12%);
    }
  }

  &--secondary {
    background: color.scale($surface, $lightness: 8%);
    border-color: color.scale($surface, $lightness: -6%);

    &:hover:not(.is-disabled):not(.is-loading) {
      background: color.scale($surface, $lightness: 12%);
      border-color: color.scale($surface, $lightness: -2%);
    }

    &:active:not(.is-disabled):not(.is-loading) {
      background: color.scale($surface, $lightness: 4%);
    }
  }

  &--danger {
    $base: color.scale($rarity-covert, $lightness: -8%);
    background: $base;
    border-color: color.scale($rarity-covert, $lightness: -18%);
    color: $text-light-200;
    box-shadow:
      0 0 0 1px color.scale($rarity-covert, $lightness: -22%),
      0 6px 18px rgba(0, 0, 0, 0.35);

    &:hover:not(.is-disabled):not(.is-loading) {
      background: color.scale($rarity-covert, $lightness: -2%);
      border-color: color.scale($rarity-covert, $lightness: -14%);
    }

    &:active:not(.is-disabled):not(.is-loading) {
      background: color.scale($rarity-covert, $lightness: -12%);
      border-color: color.scale($rarity-covert, $lightness: -20%);
    }
  }

  &--success {
    $base: color.scale($green-button-bg, $lightness: 10%);
    background: $base;
    border-color: color.scale($accent-lime, $lightness: -4%);
    color: $text-light-200;
    box-shadow:
      0 0 0 1px color.scale($green-button-bg, $lightness: -8%),
      0 6px 18px rgba(0, 0, 0, 0.32);

    &:hover:not(.is-disabled):not(.is-loading) {
      background: color.scale($green-button-bg, $lightness: 16%);
      border-color: color.scale($accent-lime, $lightness: -8%);
    }

    &:active:not(.is-disabled):not(.is-loading) {
      background: color.scale($green-button-bg, $lightness: 4%);
      border-color: color.scale($accent-lime, $lightness: -12%);
    }
  }

  &--alert {
    $border-color: color.scale($rarity-covert, $lightness: 8%);
    background: color.change($rarity-covert, $alpha: 0.22);
    border-color: $border-color;
    color: $text-light-200;
    box-shadow:
      0 0 0 1px color.scale($rarity-covert, $lightness: -18%),
      0 8px 20px rgba(197, 60, 60, 0.28);

    &:hover:not(.is-disabled):not(.is-loading) {
      background: color.change(color.scale($rarity-covert, $lightness: 6%), $alpha: 0.28);
      border-color: color.scale($rarity-covert, $lightness: 2%);
    }

    &:active:not(.is-disabled):not(.is-loading) {
      background: color.change(color.scale($rarity-covert, $lightness: -6%), $alpha: 0.3);
      border-color: color.scale($rarity-covert, $lightness: -4%);
      box-shadow:
        0 0 0 1px color.scale($rarity-covert, $lightness: -22%),
        0 4px 14px rgba(197, 60, 60, 0.32);
    }
  }

  &--ghost {
    background: transparent;
    border-color: transparent;

    &:hover:not(.is-disabled):not(.is-loading) {
      background: rgba(255, 255, 255, 0.08);
    }

    &:active:not(.is-disabled):not(.is-loading) {
      background: rgba(255, 255, 255, 0.12);
    }
  }

  &__inner {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 100%;
  }

  &__content {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
  }

  &__icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 0.95em;
    line-height: 0;

    &--left {
      margin-inline-end: 2px;
    }

    &--right {
      margin-inline-start: 2px;
    }
  }

  &__spinner {
    width: 16px;
    height: 16px;
    border-radius: 50%;
    border: 2px solid rgba(255, 255, 255, 0.4);
    border-top-color: $text-light-200;
    animation: spin 0.8s linear infinite;
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
